import { Fragment, useState, type ReactNode } from 'react';

import {
  Callout,
  Chip,
  Empty,
  Field,
  Loading,
  Modal,
  Panel,
  TableScroll,
  TextInput,
  explainFailure,
  formatDateTime,
  isApiClientError,
  kitText,
  useLoad,
} from '@affdice/portal-kit';

import { PlatformApi } from '../../client.ts';
import { t, tCount } from '../../messages.ts';
import {
  capabilityLabel,
  draftDiff,
  groupCapabilities,
  historyLine,
  matrixRoles,
  roleLabel,
  type CapabilityHistoryRow,
  type CapabilityMatrix,
  type PlatformUserRow,
} from './roles-permissions-model.ts';

/**
 * Platform → Roles & permissions (owner decision 2, Rule Book §3.12).
 *
 * Capabilities down the side, platform roles across the top, a checkbox in
 * each cell. The super admin has no column: it holds every permission and
 * nothing here can change that, which a note above the table says once. A
 * column for a role the reader holds is locked — nobody widens their own
 * permissions (§3.12c). Edits
 * collect as a draft and are saved together, with a reason, after a
 * confirmation that lists exactly what each role gains and loses.
 *
 * The server enforces all of this independently; the locks here only stop a
 * control being offered that would be refused.
 */

const matrixOf = (api: PlatformApi) => api.request<CapabilityMatrix>('GET', '/v1/platform/permissions');
const historyOf = (api: PlatformApi) =>
  api.request<{ items: CapabilityHistoryRow[] }>('GET', '/v1/platform/permissions/history');

function Failure({ error }: { error: unknown }) {
  if (isApiClientError(error) && error.status === 403) {
    return <Callout tone="caution">{t('plat.roles.forbidden')}</Callout>;
  }
  return <Callout tone="error">{explainFailure(error).message}</Callout>;
}

export function RolesPermissions({ api, userId: currentUserId }: { api: PlatformApi; canWrite: boolean; userId?: string | undefined }) {
  const [version, setVersion] = useState(0);
  const matrix = useLoad(() => matrixOf(api), [version]);
  const history = useLoad(() => historyOf(api), [version]);
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [confirming, setConfirming] = useState(false);
  const [editingUser, setEditingUser] = useState<PlatformUserRow | null>(null);
  const [saved, setSaved] = useState(false);

  if (matrix.state === 'loading') {
    return (<><Head /><Loading rows={6} /></>);
  }
  if (matrix.state === 'error') {
    return (<><Head /><Failure error={matrix.error} /></>);
  }

  const m = matrix.value;
  const columns = matrixRoles(m.roles);
  const ownRoles = new Set(m.users.find((u) => u.id === currentUserId)?.roles ?? []);
  const editable = (code: string, locked: boolean) => m.can_manage && !locked && !ownRoles.has(code);
  const current = (roleCode: string): Set<string> =>
    draft[roleCode] ?? new Set(m.roles.find((r) => r.code === roleCode)?.capabilities ?? []);

  const changes = m.roles
    .filter((r) => draft[r.code])
    .map((r) => ({ role: r, ...draftDiff(r.capabilities, draft[r.code]!) }))
    .filter((c) => c.added.length + c.removed.length > 0);
  const changeCount = changes.reduce((n, c) => n + c.added.length + c.removed.length, 0);

  const toggle = (roleCode: string, cap: string, on: boolean) => {
    setSaved(false);
    setDraft((prev) => {
      const next = new Set(prev[roleCode] ?? current(roleCode));
      if (on) next.add(cap); else next.delete(cap);
      return { ...prev, [roleCode]: next };
    });
  };

  const names = (code: string) =>
    capabilityLabel(code, m.capabilities.find((c) => c.code === code)?.description);

  return (
    <>
      <Head />
      <p className="faint">{t('plat.roles.intro')}</p>
      {!m.can_manage ? <Callout>{t('plat.roles.readOnly')}</Callout> : null}
      {saved ? <Callout>{t('plat.roles.saved')}</Callout> : null}

      <div className="perm-layout">
      <Panel
        title={t('plat.roles.panel.matrix')}
        aside={changeCount > 0 ? (
          <span className="form-actions">
            <span className="faint">{tCount('plat.roles.unsaved', changeCount)}</span>
            <button type="button" className="copy" onClick={() => setDraft({})}>{t('plat.roles.action.discard')}</button>
            <button type="button" className="primary" onClick={() => setConfirming(true)}>{t('plat.roles.action.save')}</button>
          </span>
        ) : undefined}
      >
        {columns.length === 0 || m.capabilities.length === 0 ? (
          <Empty>{t('plat.roles.users.empty')}</Empty>
        ) : (
          <>
            <p className="faint">{t('plat.roles.superAdminNote')}</p>
            <TableScroll>
              <table className="perm-matrix">
                <thead>
                  <tr>
                    <th scope="col">{t('plat.roles.col.permission')}</th>
                    {columns.map((r) => (
                      <th key={r.code} scope="col" title={ownRoles.has(r.code) ? t('plat.roles.ownRole') : undefined}>
                        {roleLabel(r.code)}
                        {ownRoles.has(r.code) ? <div><Chip tone="locked">{t('plat.roles.ownRoleChip')}</Chip></div> : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupCapabilities(m.capabilities).map((g) => (
                    <Fragment key={g.group}>
                      <tr className="perm-group">
                        <th scope="colgroup" colSpan={columns.length + 1}>{t(g.group)}</th>
                      </tr>
                      {g.items.map((c) => (
                        <tr key={c.code}>
                          <th scope="row">{names(c.code)}</th>
                          {columns.map((r) => {
                            const on = current(r.code).has(c.code);
                            const can = editable(r.code, r.locked);
                            return (
                              <td key={r.code}>
                                {/* The whole cell is the click target, not the 13px box. */}
                                <label className="perm-cell">
                                  <input
                                    type="checkbox"
                                    checked={on}
                                    disabled={!can}
                                    aria-label={`${roleLabel(r.code)}: ${names(c.code)}`}
                                    onChange={(e) => toggle(r.code, c.code, e.target.checked)}
                                  />
                                </label>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </>
        )}
      </Panel>

      <div className="perm-side">
      <Panel title={t('plat.roles.panel.users')} count={m.users.length}>
        {m.users.length === 0 ? (
          <Empty>{t('plat.roles.users.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('plat.roles.users.col.person')}</th>
                  <th scope="col">{t('plat.roles.users.col.role')}</th>
                  <th scope="col">{t('plat.roles.users.col.extra')}</th>
                  {m.can_manage ? <th scope="col" aria-label={t('plat.roles.action.change')} /> : null}
                </tr>
              </thead>
              <tbody>
                {m.users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}{u.name !== u.email ? <div className="faint">{u.email}</div> : null}</td>
                    <td>{u.roles.map(roleLabel).join(', ') || '—'}</td>
                    <td>{u.extra.length ? u.extra.map(names).join(', ') : <span className="faint">{t('plat.roles.users.none')}</span>}</td>
                    {m.can_manage ? (
                      <td className="num">
                        {u.id === currentUserId ? (
                          <span className="faint">{t('plat.roles.ownAccount')}</span>
                        ) : (
                          <button type="button" className="copy" onClick={() => { setSaved(false); setEditingUser(u); }}>
                            {t('plat.roles.action.change')}
                          </button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>

      <Panel title={t('plat.roles.panel.history')}>
        {history.state === 'loading' ? <Loading rows={2} /> : null}
        {history.state === 'error' ? <Failure error={history.error} /> : null}
        {history.state === 'ready' ? (
          history.value.items.length === 0 ? (
            <Empty>{t('plat.roles.history.empty')}</Empty>
          ) : (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('plat.roles.history.col.when')}</th>
                    <th scope="col">{t('plat.roles.history.col.who')}</th>
                    <th scope="col">{t('plat.roles.history.col.what')}</th>
                    <th scope="col">{t('plat.roles.history.col.why')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.value.items.map((h) => (
                    <tr key={h.id}>
                      <td>{formatDateTime(h.at)}</td>
                      <td>{h.actor ?? t('plat.roles.history.system')}</td>
                      <td className="wrap">{historyLine(h, names)}</td>
                      <td className="wrap">{h.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )
        ) : null}
      </Panel>
      </div>
      </div>

      {confirming ? (
        <SaveDialog
          title={t('plat.roles.confirm.title')}
          lines={changes.map((c) => ({
            key: c.role.code,
            subject: roleLabel(c.role.code),
            added: c.added.map(names),
            removed: c.removed.map(names),
          }))}
          onCancel={() => setConfirming(false)}
          onSave={async (reason) => {
            for (const c of changes) {
              await api.request('PUT', `/v1/platform/permissions/roles/${encodeURIComponent(c.role.code)}`, {
                body: { capabilities: [...draft[c.role.code]!], reason },
              });
            }
            setConfirming(false);
            setDraft({});
            setSaved(true);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}

      {editingUser ? (
        <UserGrants
          user={editingUser}
          matrix={m}
          names={names}
          onCancel={() => setEditingUser(null)}
          onSave={async (capabilities, reason) => {
            await api.request('PUT', `/v1/platform/permissions/users/${encodeURIComponent(editingUser.id)}`, {
              body: { capabilities, reason },
            });
            setEditingUser(null);
            setSaved(true);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </>
  );
}

function Head() {
  return <div className="page-head"><h1>{t('nav.roles')}</h1></div>;
}

/** The confirmation: what changes, for whom, and why — the reason is required. */
function SaveDialog({
  title, lines, onCancel, onSave, children,
}: {
  title: string;
  lines: { key: string; subject: string; added: string[]; removed: string[] }[];
  onCancel: () => void;
  onSave: (reason: string) => Promise<void>;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [missing, setMissing] = useState(false);

  return (
    <Modal title={title} onClose={() => { if (!busy) onCancel(); }}>
      <div className="stack">
        {failure ? <Failure error={failure} /> : null}
        {children}
        {lines.length > 0 ? (
          <ul>
            {lines.map((l) => (
              <li key={l.key}>
                <strong>{l.subject}</strong>
                {l.added.length ? <div>{t('plat.roles.grant')}: {l.added.join(', ')}</div> : null}
                {l.removed.length ? <div>{t('plat.roles.revoke')}: {l.removed.join(', ')}</div> : null}
                {!l.added.length && !l.removed.length ? <div className="faint">{t('plat.roles.nothingChanged')}</div> : null}
              </li>
            ))}
          </ul>
        ) : null}
        <Field
          label={t('plat.roles.confirm.reason')}
          hint={t('plat.roles.confirm.reasonHint')}
          {...(missing ? { error: t('plat.roles.confirm.reasonMissing') } : {})}
        >
          <TextInput value={reason} onChange={(v) => { setReason(v); setMissing(false); }} invalid={missing} />
        </Field>
        <div className="form-actions">
          <button type="button" className="copy" disabled={busy} onClick={onCancel}>{kitText('action.cancel')}</button>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => {
              if (!reason.trim()) { setMissing(true); return; }
              setBusy(true);
              setFailure(null);
              onSave(reason.trim()).catch(setFailure).finally(() => setBusy(false));
            }}
          >
            {busy ? t('plat.roles.action.working') : t('plat.roles.action.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** One person's extra permissions: what their role already gives is shown ticked and fixed. */
function UserGrants({
  user, matrix, names, onCancel, onSave,
}: {
  user: PlatformUserRow;
  matrix: CapabilityMatrix;
  names: (code: string) => string;
  onCancel: () => void;
  onSave: (capabilities: string[], reason: string) => Promise<void>;
}) {
  const fromRole = new Set(
    matrix.roles.filter((r) => user.roles.includes(r.code)).flatMap((r) => r.capabilities),
  );
  const [chosen, setChosen] = useState<Set<string>>(new Set(user.extra));
  const diff = draftDiff(user.extra, chosen);

  return (
    <SaveDialog
      title={t('plat.roles.confirm.userTitle', { name: user.name })}
      lines={diff.added.length + diff.removed.length > 0
        ? [{ key: user.id, subject: user.name, added: diff.added.map(names), removed: diff.removed.map(names) }]
        : []}
      onCancel={onCancel}
      onSave={(reason) => onSave([...chosen], reason)}
    >
      {groupCapabilities(matrix.capabilities).map((g) => (
        <Field key={g.group} group label={t(g.group)}>
          {g.items.map((c) => {
            const inherited = fromRole.has(c.code);
            return (
              <label key={c.code} className="checkbox-stack-row">
                <input
                  type="checkbox"
                  checked={inherited || chosen.has(c.code)}
                  disabled={inherited}
                  onChange={(e) => setChosen((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(c.code); else next.delete(c.code);
                    return next;
                  })}
                />
                {' '}{names(c.code)}
                {inherited ? <span className="faint"> — {t('plat.roles.fromRole')}</span> : null}
              </label>
            );
          })}
        </Field>
      ))}
    </SaveDialog>
  );
}
